





















































































































































import { Component, Prop, Vue, Watch, Inject } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { RawLocation } from "vue-router";

import LoadingSpinner from "@/lib/components/LoadingSpinner.vue";
import ListItemPageNavigation from "@/lib/components/ListItemPageNavigation.vue";

import dataStore from "@/store";
import AuthenticatedUserProvider from "@/modules/account/authenticated-user-provider.service";
import { Resource } from "@/modules/account/resource";
import AuthenticatedUser from "@/modules/account/authenticated-user.model";
import { ApiFilterValue } from "@/modules/api/api-filter-value.type";
import { Dictionary } from "@/lib/Dictionary.type";

import FactoryInvoiceViewInfo from "./FactoryInvoiceView/Info.vue";
import FactoryInvoiceViewCounterpartiesInfo from "./FactoryInvoiceView/CounterpartiesInfo.vue";
import FactoryInvoiceViewSummary from "./FactoryInvoiceView/Summary.vue";
import FactoryInvoiceViewPaymentSummary from "./FactoryInvoiceView/PaymentSummary.vue";
import FactoryInvoiceViewAddExtraPaymentForm from "./FactoryInvoiceView/AddExtraPaymentForm.vue";
import FactoryInvoiceViewExtraPaymentList from "./FactoryInvoiceView/ExtraPaymentList.vue";
import FactoryInvoiceViewDetails from "./FactoryInvoiceView/Details.vue";
import FactoryInvoicePublishAction from "./FactoryInvoiceView/PublishAction.vue";
import FactoryInvoiceApproveAction from "./FactoryInvoiceView/ApproveAction.vue";
import FactoryInvoiceDownloadPdfAction from "./FactoryInvoiceView/DownloadPdfAction.vue";
import ProductionTimeHistoryTable from "./ProductionTimeHistoryTable.vue";
import BatchInvoiceShippingForm from "./BatchInvoiceShippingForm.vue";

import { FactoryInvoiceStatusValue } from "../factory-invoice-status.value";
import FactoryInvoice from "../factory-invoice.model";
import FactoryInvoiceStore from "../store/index";
import InvoiceItemElement from "../invoice-item-element.model";
import InvoiceItem from "../invoice-item.model";
import BatchInvoice from "../batch-invoice.model";
import BulkProjectStore from "../../bulk-project/store";
import BulkProject from "../../bulk-project/bulk-project.model";
import {
  batchFactoryInvoiceTypes,
  FactoryInvoiceTypeValue,
  regularFactoryInvoiceTypes,
} from "../factory-invoice-type.value";

@Component({
  metaInfo() {
    const component = this as FactoryInvoiceViewPage;

    return {
      title: component.invoice
        ? `${component.isBatchScope ? "Batch Invoice" : "Factory Invoice"} #${
            component.invoice.number
          }`
        : component.isBatchScope
        ? "Batch Invoice"
        : "Factory Invoice",
    };
  },
  components: {
    FactoryInvoiceViewInfo,
    FactoryInvoiceViewCounterpartiesInfo,
    FactoryInvoiceViewSummary,
    FactoryInvoiceViewPaymentSummary,
    FactoryInvoiceViewAddExtraPaymentForm,
    FactoryInvoiceViewExtraPaymentList,
    FactoryInvoiceViewDetails,
    FactoryInvoicePublishAction,
    FactoryInvoiceApproveAction,
    FactoryInvoiceDownloadPdfAction,
    LoadingSpinner,
    ListItemPageNavigation,
    ProductionTimeHistoryTable,
    BatchInvoiceShippingForm,
  },
})
export default class FactoryInvoiceViewPage extends Vue {
  @Prop({ required: true })
  public readonly invoiceId!: string;

  @Prop({ default: false })
  public readonly isBatchScope!: boolean;

  @Prop()
  public readonly query!: any;

  @Prop()
  public readonly listQuery?: Dictionary<string[]>;

  @Inject("AuthenticatedUserProvider")
  private fUserProvider!: AuthenticatedUserProvider;

  private fIsLoading = false;
  private fIsProductionTimeHistoryExpanded = false;

  private fFactoryInvoiceStore: FactoryInvoiceStore;
  private fBulkProjectStore: BulkProjectStore;
  private fCounterpartInvoice: FactoryInvoice | null = null;

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  get isFactoryMode(): boolean {
    const user = this.fUserProvider.getUser();

    if (!user) {
      return true;
    }

    return user.role === AuthenticatedUser.ROLE_FACTORY;
  }

  get invoice(): FactoryInvoice | undefined {
    return this.fFactoryInvoiceStore.getFactoryInvoiceById(this.invoiceId);
  }

  get batchInvoice(): BatchInvoice | undefined {
    return this.fFactoryInvoiceStore.getBatchInvoiceByInvoiceId(this.invoiceId);
  }

  get bulkProject(): BulkProject | undefined {
    if (!this.canViewBulkProject || !this.batchInvoice) {
      return undefined;
    }

    return this.fBulkProjectStore.getBulkProjectById(
      this.batchInvoice.bulkProjectId
    );
  }

  get counterpartInvoice(): FactoryInvoice | undefined {
    return this.fCounterpartInvoice || undefined;
  }

  get canViewBulkProject(): boolean {
    const user = this.fUserProvider.getUser();

    return !!user && user.hasPermissionForResource(Resource.BULK_PROJECT_VIEW);
  }

  get batchTypeLabel(): string {
    if (!this.invoice) {
      return "";
    }

    if (this.invoice.type === FactoryInvoiceTypeValue.BATCH_BALANCE) {
      return "Balance";
    }

    if (
      this.invoice.type === FactoryInvoiceTypeValue.BATCH_DEPOSIT &&
      this.batchInvoice
    ) {
      return `${this.batchInvoice.usedDepositPercent}% Deposit`;
    }

    return "";
  }

  get shippingAmount(): number | undefined {
    if (this.invoice?.type !== FactoryInvoiceTypeValue.BATCH_BALANCE) {
      return undefined;
    }

    return this.batchInvoice?.shippingAmount;
  }

  get listPageRouteName(): string {
    return this.isBatchScope
      ? "BatchInvoicesListPage"
      : "FactoryInvoicesListPage";
  }

  get viewPageRouteName(): string {
    return this.isBatchScope
      ? "BatchInvoiceViewPage"
      : "FactoryInvoiceViewPage";
  }

  get isShippingEditable(): boolean {
    if (
      !this.isBatchScope ||
      !this.invoice ||
      this.invoice.type !== FactoryInvoiceTypeValue.BATCH_BALANCE
    ) {
      return false;
    }

    const user = this.fUserProvider.getUser();

    if (!user || !user.hasPermissionForResource(Resource.INVOICE_MANAGE)) {
      return false;
    }

    return [
      FactoryInvoiceStatusValue.DRAFT,
      FactoryInvoiceStatusValue.REVIEW,
    ].includes(this.invoice.status);
  }

  get isShippingFormVisible(): boolean {
    return this.isShippingEditable && !this.shippingAmount;
  }

  get isProductionTimeHistoryExpanded(): boolean {
    return this.fIsProductionTimeHistoryExpanded;
  }

  get canViewCounterpartiesInfo(): boolean {
    const user = this.fUserProvider.getUser();

    if (!user) {
      return false;
    }

    return user.hasPermissionForResource(Resource.FACTORY_BILLING_INFO_READ);
  }

  get plushieIds(): string[] {
    const invoiceItems = this.fFactoryInvoiceStore.getInvoiceItemsByInvoiceId(
      this.invoiceId
    );

    return invoiceItems.map((item) => item.plushie);
  }

  get showPaymentSummary(): boolean {
    const invoice = this.fFactoryInvoiceStore.getFactoryInvoiceById(
      this.invoiceId
    );

    if (!invoice) {
      return false;
    }

    if (invoice.status !== FactoryInvoiceStatusValue.PAID) {
      return false;
    }

    const user = this.fUserProvider.getUser();

    if (!user) {
      return false;
    }

    return user.hasPermissionForResource(
      Resource.FACTORY_INVOICE_PAYMENTS_READ
    );
  }

  get canAddExtraPayments(): boolean {
    if (!this.invoice) {
      return false;
    }

    const user = this.fUserProvider.getUser();

    if (!user) {
      return false;
    }

    if (
      !user.hasPermissionForResource(
        Resource.FACTORY_INVOICE_EXTRA_PAYMENTS_MANAGE
      )
    ) {
      return false;
    }

    if (user.role === AuthenticatedUser.ROLE_FACTORY) {
      return this.invoice.status === FactoryInvoiceStatusValue.DRAFT;
    } else if (
      [AuthenticatedUser.ROLE_ADMIN, AuthenticatedUser.ROLE_STORE].includes(
        user.role
      )
    ) {
      return [
        FactoryInvoiceStatusValue.DRAFT,
        FactoryInvoiceStatusValue.REVIEW,
      ].includes(this.invoice.status);
    }

    return false;
  }

  get isPublishingAvailable(): boolean {
    const invoice = this.fFactoryInvoiceStore.getFactoryInvoiceById(
      this.invoiceId
    );

    if (!invoice || invoice.hasUnpricedElements) {
      return false;
    }

    if (invoice.status !== FactoryInvoiceStatusValue.DRAFT) {
      return false;
    }

    const user = this.fUserProvider.getUser();

    if (!user) {
      return false;
    }

    return user.hasPermissionForResource(Resource.INVOICE_PUBLISH);
  }

  get isApprovingAvailable(): boolean {
    const invoice = this.fFactoryInvoiceStore.getFactoryInvoiceById(
      this.invoiceId
    );

    if (!invoice) {
      return false;
    }

    if (invoice.status !== FactoryInvoiceStatusValue.REVIEW) {
      return false;
    }

    const user = this.fUserProvider.getUser();

    if (!user) {
      return false;
    }

    return user.hasPermissionForResource(Resource.INVOICE_APPROVE);
  }

  get isDownloadingPdfAvailable(): boolean {
    const invoice = this.fFactoryInvoiceStore.getFactoryInvoiceById(
      this.invoiceId
    );

    if (!invoice) {
      return false;
    }

    return [
      FactoryInvoiceStatusValue.UNPAID,
      FactoryInvoiceStatusValue.PAID,
    ].includes(invoice.status);
  }

  get showProductionTimeHistory(): boolean {
    const user = this.fUserProvider.getUser();

    if (!user) {
      return false;
    }

    return user.hasPermissionForResource(Resource.PRODUCTION_TIME_HISTORY_READ);
  }

  get showUnpricedElementsHint(): boolean {
    return !!this.invoice && this.invoice.hasUnpricedElements;
  }

  get showUnpricedElements(): boolean {
    return !this.isFactoryMode;
  }

  get invoiceItems(): InvoiceItem[] {
    return this.fFactoryInvoiceStore.getInvoiceItemsByInvoiceId(this.invoiceId);
  }

  get unpricedElements(): InvoiceItemElement[] {
    const itemIds = this.invoiceItems.map((item) => item.id);

    const unpricedElements: Dictionary<InvoiceItemElement> = {};

    for (const itemId of itemIds) {
      const elements = this.fFactoryInvoiceStore.getInvoiceItemElementsByItemId(
        itemId
      );

      for (const element of elements) {
        if (element.noPriceDefined) {
          unpricedElements[element.name] = element;
        }
      }
    }

    return Object.values(unpricedElements);
  }

  get priceConfigurationPageLink(): RawLocation | undefined {
    if (!this.invoice) {
      return;
    }

    return {
      name: "PriceConfigurationEditPage",
      query: {
        id: this.invoice.factory,
      },
    };
  }

  constructor() {
    super();

    this.fFactoryInvoiceStore = getModule(FactoryInvoiceStore, dataStore);
    this.fBulkProjectStore = getModule(BulkProjectStore, dataStore);
  }

  public getInvoicesOrder(): Promise<string[]> {
    const filter: Dictionary<ApiFilterValue> = {
      ...this.query?.filter,
    };
    const selectedType = filter.type;

    if (
      this.isBatchScope &&
      selectedType !== undefined &&
      selectedType !== ""
    ) {
      filter.type = selectedType;
    } else if (this.isBatchScope) {
      filter.type = batchFactoryInvoiceTypes;
    } else {
      filter.type = regularFactoryInvoiceTypes;
    }

    return this.fFactoryInvoiceStore.loadFactoryInvoiceIds({
      order: this.query?.order,
      filter,
    });
  }

  public toggleProductionTimeHistory(): void {
    this.fIsProductionTimeHistoryExpanded = !this
      .fIsProductionTimeHistoryExpanded;
  }

  private async getInvoiceData(invoiceId: string): Promise<void> {
    this.fCounterpartInvoice = null;

    const [factoryInvoice, factoryInvoiceItems] = await Promise.all([
      this.fFactoryInvoiceStore.loaFactoryInvoiceById({
        id: invoiceId,
        useCache: false,
      }),
      this.fFactoryInvoiceStore.loadInvoiceItemsByInvoiceId({ invoiceId }),
      this.fFactoryInvoiceStore.loadInvoiceStatuses(),
      this.fFactoryInvoiceStore.loadExtraPaymentFaults(),
    ]);

    if (!factoryInvoice) {
      return;
    }

    const itemIds = factoryInvoiceItems.map((item) => item.id);

    await this.fFactoryInvoiceStore.loadElementsByInvoiceItemIds({ itemIds });

    if (!this.isBatchScope) {
      return;
    }

    const batchInvoice = await this.fFactoryInvoiceStore.loadBatchInvoiceByInvoiceId(
      {
        invoiceId,
      }
    );

    if (!batchInvoice) {
      return;
    }

    const [, counterpartInvoice] = await Promise.all([
      this.canViewBulkProject
        ? this.fBulkProjectStore.loadBulkProjectById(batchInvoice.bulkProjectId)
        : Promise.resolve(undefined),
      this.fFactoryInvoiceStore.loadCounterpartInvoice({
        invoiceId,
        batchPlushieId: batchInvoice.batchPlushieId,
        invoiceType: factoryInvoice.type,
      }),
    ]);

    this.fCounterpartInvoice = counterpartInvoice || null;
  }

  @Watch("invoiceId", { immediate: true })
  private async _onInvoiceIdChange() {
    if (!this.invoiceId) {
      return;
    }

    this.fIsLoading = true;

    try {
      await this.getInvoiceData(this.invoiceId);
    } finally {
      this.fIsLoading = false;
    }
  }
}
